import React, { useState } from 'react';
import { Info } from 'lucide-react';
import InputField from 'ui/InputField';
import api from 'config/axiosConfig';

interface PasswordRecoveryProps {
  email: string;
  onBack: () => void;
  onClose: () => void;
}

const PasswordRecovery: React.FC<PasswordRecoveryProps> = ({ email, onBack, onClose }) => {
  const [emailOrUsername, setEmailOrUsername] = useState(email);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);

  const handlePasswordReset = async () => {
    if (!emailOrUsername || emailOrUsername.length < 2) {
      setResetError("Please enter a valid email or username (minimum 2 characters)");
      return;
    }
    setResetError(null);
    setIsSubmittingReset(true);
    try {
      await api.post("/auth/password/forgot", { emailOrUsername });
      setIsResetSuccess(true);
    } catch (error: any) {
      setResetError(error.response?.data?.message || "Failed to send reset instructions");
    } finally {
      setIsSubmittingReset(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
      {isResetSuccess ? (
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            An email with a link to reset your password was sent to the email address associated with your account.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-grow flex-col justify-end place-items-center pb-8 mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:place-items-end sm:mb-0">
            <p className="text-eoe-text-meta dark:text-eoe-dark-text-meta text-sm">
                Don't see the email? Check your spam folder, or <span onClick={handlePasswordReset} className="text-eoe-text-meta dark:text-eoe-dark-text-meta font-semibold cursor-pointer underline">resend the email</span>.
            </p>
            <button 
              type="button"
              className="btn-primary w-full sm:w-fit py-2 px-4" 
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            Enter your email address or username and we'll send you a link to reset your password.
          </p>

          <form className="flex flex-col flex-grow space-y-4">
            <div className="form-control mb-0">
              <InputField
                type="text"
                id="emailOrUsername"
                name="emailOrUsername"
                labelText="Email or username"
                fieldRequired
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                validationState={emailOrUsername.length >= 2 ? 'valid' : emailOrUsername ? 'invalid' : 'none'}
                spellCheck={false}
              />
            </div>

            {resetError && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                <Info className="mr-1 size-4" />
                <span>{resetError}</span>
              </div>
            )}
          </form>

          {/* Action Buttons */}
          <div className="flex flex-grow flex-col justify-end place-items-end pb-8 mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row">
            <button
              type="button"
              className="btn-secondary w-full sm:w-fit py-2 px-4"
              onClick={onBack}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handlePasswordReset}
              disabled={isSubmittingReset || emailOrUsername.length < 2}
              className={`w-full sm:w-fit py-2 px-4 ${
                isSubmittingReset || emailOrUsername.length < 2 ? 'btn-primary-disabled' : 'btn-primary'
              } ${isSubmittingReset ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmittingReset ? "Submitting..." : "Reset password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordRecovery;
